/**
 * Background presets for the background remover — the same palette sohozkaj-site
 * ships (src/lib/data/removeBackgroundPresets.js), so a picture edited on either
 * side can get the same colour.
 */

/** Solid colours. White leads: it is the one an ID or passport photo asks for. */
export const BACKGROUND_COLOR_SWATCHES = [
    { id: 'white', value: '#FFFFFF', label: 'সাদা' },
    { id: 'red', value: '#F44336', label: 'লাল' },
    { id: 'pink', value: '#E91E8C', label: 'গোলাপি' },
    { id: 'purple', value: '#9C27B0', label: 'বেগুনি' },
    { id: 'indigo', value: '#673AB7', label: 'ইন্ডিগো' },
    { id: 'blue', value: '#3F51B5', label: 'নীল' },
    { id: 'sky', value: '#2196F3', label: 'আকাশি নীল' },
    { id: 'cyan', value: '#03A9F4', label: 'সায়ান' },
    { id: 'teal', value: '#009688', label: 'টিল' },
    { id: 'forest', value: '#2E7D32', label: 'গাঢ় সবুজ' },
    { id: 'green', value: '#4CAF50', label: 'সবুজ' },
    { id: 'light-green', value: '#8BC34A', label: 'হালকা সবুজ' },
    { id: 'lime', value: '#CDDC39', label: 'লাইম' },
    { id: 'yellow', value: '#FFEB3B', label: 'হলুদ' },
    { id: 'amber', value: '#FFC107', label: 'অ্যাম্বার' },
    { id: 'orange', value: '#FF9800', label: 'কমলা' },
    { id: 'deep-orange', value: '#FF5722', label: 'গাঢ় কমলা' },
    { id: 'brown', value: '#795548', label: 'বাদামি' },
    { id: 'gray', value: '#9E9E9E', label: 'ধূসর' },
];

/**
 * Gradients, plus one checker pattern. `angle` follows the canvas convention the
 * site uses: 0° runs left → right, 90° top → bottom.
 */
export const BACKGROUND_COLOR_PATTERNS = [
    { id: 'sky', label: 'আকাশ', angle: 180, stops: [['#38BDF8', 0], ['#E0F2FE', 1]] },
    { id: 'sunset', label: 'সূর্যাস্ত', angle: 135, stops: [['#F97316', 0], ['#FDE68A', 0.55], ['#EC4899', 1]] },
    { id: 'forest', label: 'বন', angle: 160, stops: [['#14532D', 0], ['#4ADE80', 0.45], ['#166534', 1]] },
    { id: 'ocean', label: 'সমুদ্র', angle: 180, stops: [['#0C4A6E', 0], ['#0284C7', 0.5], ['#7DD3FC', 1]] },
    { id: 'studio', label: 'স্টুডিও', angle: 180, stops: [['#F3F4F6', 0], ['#D1D5DB', 1]] },
    { id: 'warm', label: 'উষ্ণ', angle: 45, stops: [['#FEF3C7', 0], ['#FDBA74', 1]] },
    { id: 'purple', label: 'গ্রেডিয়েন্ট', angle: 135, stops: [['#581C87', 0], ['#A855F7', 0.5], ['#DDD6FE', 1]] },
    { id: 'brick', label: 'প্যাটার্ন', type: 'pattern', base: '#B45309', accent: '#92400E' },
    { id: 'sand', label: 'বালু', angle: 180, stops: [['#D6D3D1', 0], ['#F5F5F4', 0.4], ['#A8A29E', 1]] },
    { id: 'night', label: 'রাত', angle: 180, stops: [['#0F172A', 0], ['#1E293B', 0.6], ['#334155', 1]] },
    { id: 'mint', label: 'মিন্ট', angle: 120, stops: [['#99F6E4', 0], ['#5EEAD4', 0.5], ['#2DD4BF', 1]] },
    { id: 'rose', label: 'গোলাপ', angle: 135, stops: [['#FDA4AF', 0], ['#FB7185', 0.5], ['#BE123C', 1]] },
    { id: 'silver', label: 'রুপালি', angle: 180, stops: [['#F9FAFB', 0], ['#D1D5DB', 0.45], ['#9CA3AF', 1]] },
    { id: 'peach', label: 'পীচ', angle: 160, stops: [['#FFF7ED', 0], ['#FED7AA', 0.55], ['#FB923C', 1]] },
    { id: 'cyan', label: 'সায়ান', angle: 135, stops: [['#ECFEFF', 0], ['#22D3EE', 0.5], ['#0891B2', 1]] },
];

/** Photo backgrounds the site hosts, all eighteen; picking one needs no upload. */
function hostedPhoto(id, label) {
    const url = `https://files.sohozkaj.com/remove-bg-presets/${id}.webp`;
    return { id, label, url, thumb: url };
}

export const BACKGROUND_PHOTO_PRESETS = [
    hostedPhoto('tropical-beach', 'ট্রপিক্যাল সৈকত'),
    hostedPhoto('green-terrace', 'সবুজ ধান ক্ষেত'),
    hostedPhoto('misty-mountains', 'কুয়াশা পাহাড়'),
    hostedPhoto('snow-lake', 'বরফের হ্রদ'),
    hostedPhoto('forest-path', 'বনের পথ'),
    hostedPhoto('modern-room', 'আধুনিক রুম'),
    hostedPhoto('living-room', 'লিভিং রুম'),
    hostedPhoto('cozy-home', 'আরামদায়ক ঘর'),
    hostedPhoto('balcony', 'বারান্দা'),
    hostedPhoto('office', 'অফিস'),
    hostedPhoto('cafe', 'ক্যাফে'),
    hostedPhoto('bookstore', 'বইয়ের দোকান'),
    hostedPhoto('library', 'লাইব্রেরি'),
    hostedPhoto('hallway', 'হলওয়ে'),
    hostedPhoto('city-skyline', 'শহর'),
    hostedPhoto('eiffel-night', 'আইফেল টাওয়ার'),
    hostedPhoto('street-up', 'উঁচু ভবন'),
    hostedPhoto('wood-wall', 'কাঠের দেয়াল'),
];

/**
 * The same preset as a CSS background, for the swatch tile. CSS measures 0° as
 * "to top" and the canvas as "to the right", hence the +90°.
 */
export function patternCss(preset) {
    if (!preset) return undefined;
    if (preset.type === 'pattern') {
        return `repeating-conic-gradient(${preset.accent} 0 25%, ${preset.base} 0 50%) 0 0 / 16px 16px`;
    }
    const stops = (preset.stops || []).map(([color, pos]) => `${color} ${Math.round(pos * 100)}%`).join(', ');
    return `linear-gradient(${(preset.angle ?? 180) + 90}deg, ${stops})`;
}

/** Paint a preset over the whole canvas — mirrors the site's compositor. */
export function drawPatternBackground(ctx, width, height, preset) {
    if (preset.type === 'pattern') {
        ctx.fillStyle = preset.base || '#B45309';
        ctx.fillRect(0, 0, width, height);
        const tile = 24;
        ctx.fillStyle = preset.accent || '#92400E';
        for (let y = 0; y < height + tile; y += tile * 2) {
            for (let x = 0; x < width + tile; x += tile * 2) {
                ctx.fillRect(x, y, tile, tile);
                ctx.fillRect(x + tile, y + tile, tile, tile);
            }
        }
        return;
    }

    const angleRad = ((preset.angle ?? 180) * Math.PI) / 180;
    const x0 = width / 2 - (Math.cos(angleRad) * width) / 2;
    const y0 = height / 2 - (Math.sin(angleRad) * height) / 2;
    const x1 = width / 2 + (Math.cos(angleRad) * width) / 2;
    const y1 = height / 2 + (Math.sin(angleRad) * height) / 2;
    const gradient = ctx.createLinearGradient(x0, y0, x1, y1);
    for (const [color, pos] of preset.stops || []) gradient.addColorStop(pos, color);
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);
}
